// ============================================================
// THE LOOKS ON THE GROUND (plan body-map M1; tools/plate-art/canvas/map/
// auras.html): the field's hex rows, the psyker's runes and embers, the
// conduit's pool, regeneration climbing, Toxic and Undying's pulses,
// Natural Armour's plates, From Beyond's window, the daemon's shadow and the
// nightmare's. Bands nest outward by lookSlots; every motion is CSS
// (DisplayLooks.module.css), so none runs with motion off.
//
// A marker redraws on every camera frame, but a look's drawing moves only
// with its transform: the many-piece drawings (tiles, runes, stars, plates,
// embers) are memoised on the ring's size and seed, under a thin outer group
// that alone follows the figure.
// ============================================================

import { memo } from 'react';
import { arcSegment } from '../../../engine/battlefield/gauge';
import { nightLimbs, runeStrokes, voidStars } from '../../../engine/display/looks';
import lk from '../../../styles/DisplayLooks.module.css';
import { groundFrame, height, metres, onCircle, px, squash, TAU, type LookPlace, type Vars } from './place';

/** One hexagon centred at `(cx, cy)`, turned to face along the ring. */
const hexPoints = (cx: number, cy: number, s: number, a: number) =>
  Array.from(
    { length: 6 },
    (_, j) => `${px(cx + s * Math.cos(a + (j * Math.PI) / 3))},${px(cy + s * Math.sin(a + (j * Math.PI) / 3))}`,
  ).join(' ');

/** A row of `n` hex tiles at radius `r` px. */
function HexRow({ r, n, s, className }: { r: number; n: number; s: number; className: string }) {
  return (
    <g className={className}>
      {Array.from({ length: n }, (_, i) => {
        const a = (i / n) * TAU;
        const [x, y] = onCircle(r, a);
        return <polygon key={i} className={lk.hex} points={hexPoints(x, y, s, a)} />;
      })}
    </g>
  );
}

/** The force field: two rows of hex tiles at its feet, turning opposite ways, the outer breathing. */
export function FieldBand({ p, radii }: { p: LookPlace; radii: number[] }) {
  return (
    <g transform={groundFrame(p)} data-look-part="field">
      <FieldTiles rx={p.rx} r0={radii[0]} r1={radii[1]} />
    </g>
  );
}

const FieldTiles = memo(function FieldTiles({ rx, r0, r1 }: { rx: number; r0: number; r1: number }) {
  const s = metres({ rx }, 0.15);
  return (
    <>
      <HexRow r={r0 * rx} n={14} s={s} className={lk.spin} />
      <g className={lk.outerRow}>
        <HexRow r={r1 * rx} n={20} s={s} className={lk.spinBack} />
      </g>
    </>
  );
});

/** The psyker's ring of rune strokes, turning. */
export function RuneBand({ p, r, seed }: { p: LookPlace; r: number; seed: string }) {
  return (
    <g transform={groundFrame(p)} data-look-part="psyker">
      <Runes R={r * p.rx} seed={seed} />
    </g>
  );
}

const Runes = memo(function Runes({ R, seed }: { R: number; seed: string }) {
  return (
    <g className={lk.runes}>
      {runeStrokes(seed).map((pts, i) => (
        <polyline key={i} className={lk.rune} points={pts.map(([x, y]) => `${px(x * R)},${px(y * R)}`).join(' ')} />
      ))}
    </g>
  );
});

/** The psyker's embers: twelve rising off the ground round it, each in its own turn. */
export function Embers({ p }: { p: LookPlace }) {
  return (
    <g transform={`translate(${px(p.foot.x)} ${px(p.foot.y)})`} data-look-part="embers">
      {/* The rise in whole pixels: under perspective it changes with depth, and each change restyles every ember. */}
      <EmberField rx={p.rx} ky={squash(p)} rise={Math.round(-height(p, 1.7))} />
    </g>
  );
}

const EmberField = memo(function EmberField({ rx, ky, rise }: { rx: number; ky: number; rise: number }) {
  return Array.from({ length: 12 }, (_, i) => {
    const a = i * 2.4;
    const d = metres({ rx }, 0.3 + 0.6 * ((i * 0.618) % 1));
    const style: Vars = { '--rise': `${rise}px`, animationDelay: `${-(i * 0.083 * 2.86).toFixed(2)}s` };
    return (
      <circle
        key={i}
        className={lk.ember}
        cx={px(d * Math.cos(a))}
        cy={px(d * Math.sin(a) * ky)}
        r={1.3}
        style={style}
      />
    );
  });
});

/** Warp Conduit's violet pool at its feet, breathing. */
export function ConduitPool({ p, r }: { p: LookPlace; r: number }) {
  return (
    <g transform={groundFrame(p)} data-look-part="conduit">
      <circle className={lk.pool} r={px(r * p.rx)} />
    </g>
  );
}

/** Regeneration: two rings climbing it from the ground to its head, shrinking as they go. */
export function RegenClimb({ p }: { p: LookPlace }) {
  const ky = squash(p);
  const style = (delay: number): Vars => ({
    '--climb': `${px(-(p.foot.y - p.head.y) / ky)}px`,
    animationDelay: `${delay}s`,
  });
  return (
    <g transform={groundFrame(p)} data-look-part="regen">
      <circle className={lk.climb} r={px(p.rx)} style={style(0)} />
      <circle className={lk.climb} r={px(p.rx)} style={style(-1)} />
    </g>
  );
}

/** A pulse ring's radius, `k` rings out, as a CSS length. */
const ringPx = (p: LookPlace, k: number) => `${px(k * p.rx)}px`;

/** Toxic's heartbeat: two rings out from outside the last band. */
export function ToxicPulse({ p, pulse0 }: { p: LookPlace; pulse0: number }) {
  return (
    <g transform={groundFrame(p)} data-look-part="pulses">
      {[0, 0.22].map((d) => (
        <circle
          key={d}
          className={lk.toxicRing}
          r={px(pulse0 * p.rx)}
          style={{ '--from': ringPx(p, pulse0), '--to': ringPx(p, pulse0 + 1.56), animationDelay: `${d}s` } as Vars}
        />
      ))}
    </g>
  );
}

/** Undying's pulse, running inward to outside the last band. */
export function UndyingPulse({ p, pulse0 }: { p: LookPlace; pulse0: number }) {
  return (
    <g transform={groundFrame(p)} data-look-part="pulses">
      <circle
        className={lk.undyingRing}
        r={px(pulse0 * p.rx)}
        style={{ '--from': ringPx(p, pulse0 + 1.5), '--to': ringPx(p, pulse0) } as Vars}
      />
    </g>
  );
}

/** An arc of a ring of radius `r` px between two angles (radians from 3 o'clock, clockwise). */
const arc = (r: number, a0: number, a1: number) =>
  // arcSegment measures turns from 12 o'clock: a quarter turn on.
  arcSegment({ cx: 0, cy: 0, rx: r, ry: r }, a0 / TAU + 0.25, (a1 - a0) / TAU);

/** Natural Armour: its ring drawn as ten scale plates, a sheen passing round them. */
export function ScaledRing({ p }: { p: LookPlace }) {
  return (
    <g transform={groundFrame(p)} data-look-part="natarm">
      <Plates r={p.rx} />
    </g>
  );
}

const Plates = memo(function Plates({ r }: { r: number }) {
  return (
    <>
      <circle className={lk.plateFill} r={px(r)} />
      {Array.from({ length: 10 }, (_, i) => {
        const a0 = (i / 10) * TAU + 0.05;
        return <path key={i} className={lk.plate} d={arc(r, a0, a0 + 0.53)} />;
      })}
      <path className={lk.sheen} d={arc(r, -0.4, 0.4)} />
    </>
  );
});

/** From Beyond's ground: its ring a black window onto a still sky. */
export function VoidRing({ p, seed, id }: { p: LookPlace; seed: string; id: string }) {
  return (
    <g transform={groundFrame(p)} data-look-part="beyond">
      <VoidWindow r={p.rx} seed={seed} clip={`look-void-ring-${id}`} star={1} />
    </g>
  );
}

/**
 * A black window of radius `r` px onto the token's still sky (its ground and its head see the same one), its stars
 * scaled by `star`.
 */
export const VoidWindow = memo(function VoidWindow(w: { r: number; seed: string; clip: string; star: number }) {
  return (
    <>
      <clipPath id={w.clip}>
        <circle r={px(w.r)} />
      </clipPath>
      <circle className={lk.void} r={px(w.r)} />
      <g clipPath={`url(#${w.clip})`}>
        {voidStars(w.seed).map((s, i) => (
          <circle
            key={i}
            className={s.warm ? lk.starWarm : lk.star}
            cx={px(s.x * w.r)}
            cy={px(s.y * w.r)}
            r={s.r * w.star}
          />
        ))}
      </g>
      <circle className={lk.voidRim} r={px(w.r)} />
    </>
  );
});

/** The daemon's shadow, falling toward the light instead of away from it. */
export function DaemonShadow({ p }: { p: LookPlace }) {
  const k = p.rx / 16.6;
  return (
    <g transform={`translate(${px(-9 * k)} ${px(-6 * k)})`} data-look-part="daemon-shadow">
      <ellipse
        className={lk.daemonShadow}
        cx={px(p.foot.x)}
        cy={px(p.foot.y)}
        rx={px(p.rx * 0.95)}
        ry={px(p.ry * 0.86)}
      />
    </g>
  );
}

/** The nightmare's shadow figure: cast on the ground (toward the light with a daemon), its head and arms its own. */
export function NightShadow({ p, seed, toLight }: { p: LookPlace; seed: string; toLight: boolean }) {
  const dir = Math.atan2(0.7, 0.6) + (toLight ? Math.PI : 0);
  const len = metres(p, 1.15);
  const [nx, ny] = onCircle(len * 0.75, dir);
  const [sx, sy] = onCircle(len * 0.6, dir);
  const [head, left, right] = nightLimbs(seed);
  return (
    <g transform={groundFrame(p)} data-look-part="night">
      <circle className={lk.nightShadow} r={px(p.rx * 0.95)} />
      <Limb at={[0, 0]} to={onCircle(len * 0.75, dir)} w={5} still />
      <Limb at={[nx, ny]} to={onCircle(len * 0.25, dir)} w={6} swing={head} />
      <Limb at={[sx, sy]} to={onCircle(metres(p, 0.55), dir + 1.3)} w={4} swing={left} />
      <Limb at={[sx, sy]} to={onCircle(metres(p, 0.55), dir - 1.3)} w={4} swing={right} />
    </g>
  );
}

interface LimbProps {
  at: [number, number];
  to: [number, number];
  w: number;
  still?: boolean;
  swing?: { swing: number; dur: number; delay: number };
}

/** One limb of the shadow figure: ink-rimmed so black reads on black, swinging about where it joins. */
function Limb({ at, to, w, still, swing }: LimbProps) {
  const style: Vars | undefined = swing && {
    '--swing': `${swing.swing}deg`,
    animationDuration: `calc(${swing.dur}s / var(--look-pace, 1))`,
    animationDelay: `${-swing.delay}s`,
  };
  return (
    <g transform={`translate(${px(at[0])} ${px(at[1])})`}>
      <g className={still ? undefined : lk.nightLimb} style={style}>
        <line className={lk.nightLimbInk} x2={px(to[0])} y2={px(to[1])} strokeWidth={w + 2} />
        <line className={lk.nightBone} x2={px(to[0])} y2={px(to[1])} strokeWidth={w} />
      </g>
    </g>
  );
}
